import {
  toggleCollapsible,
  ToggleCollapsibleEvent
} from "../../Shared/Collapsible/collapsible";

const siteHeader = document.getElementById("site-header");
const menuFlyout = document.getElementById("desktop-menu-flyout");
const searchFlyout = document.getElementById("flyout_search-desktop");

const flyoutTriggers = Array.from(
  siteHeader?.querySelectorAll<HTMLElement>("[aria-controls*='desktop-menu-flyout']") ??
    []
).filter(trigger => !!trigger);

const mainNavigationLinks = Array.from(
  siteHeader?.querySelectorAll<HTMLElement>("[data-main-navigation-link]") ?? []
).filter(link => !!link);

const menuSections = Array.from(
  menuFlyout?.querySelectorAll<HTMLElement>("[data-menu-section]") ?? []
).filter(menuSection => !!menuSection);

if (!siteHeader || !menuFlyout) {
  console.error("Main navigation is missing critical components.");
} else {
  const toggleFlyout = (e: Event) => {
    const currentTrigger = e.currentTarget as HTMLElement;
    const sectionIsActive = currentTrigger.ariaExpanded === "true";

    const shouldOpen = !sectionIsActive;

    currentTrigger.ariaExpanded = String(shouldOpen);

    // Mark other triggers as not expanded
    flyoutTriggers
      .filter(t => t !== currentTrigger)
      .forEach(t => {
        t.ariaExpanded = "false";
      });

    // Show current section, hide others
    menuSections.forEach(section => {
      section.ariaHidden = String(
        !currentTrigger.getAttribute("aria-controls")?.includes(section.id)
      );
    });

    requestAnimationFrame(() =>
      toggleCollapsible(menuFlyout, { forceState: shouldOpen })
    );

    if (
      searchFlyout &&
      currentTrigger.getAttribute("aria-controls")?.includes("flyout_search-desktop") &&
      shouldOpen
    ) {
      setTimeout(() => {
        searchFlyout.querySelector<HTMLInputElement>("input")?.focus();
      }, 200);
    }
  };

  /**
   * Tabs to the next expected element on the context (if user is currently focused inside the flyout while it's open).
   * Required as the flyout links are not nested under the trigger
   * (i.e. the default tab order is somewhat broken).
   */
  const smartTab = (event: KeyboardEvent) => {
    if (event.key === "Tab" && menuFlyout.ariaHidden === "false") {
      const ownerTriggerIndex = mainNavigationLinks.findIndex(
        t => t.ariaExpanded === "true"
      );

      const currentSection = menuSections.find(section => section.ariaHidden === "false");

      if (ownerTriggerIndex === -1 || !currentSection) {
        return;
      }

      const ownerTrigger = mainNavigationLinks[ownerTriggerIndex];

      const focusableElements = Array.from(
        currentSection.querySelectorAll<HTMLElement>("a, button, input")
      ).filter(el => el.checkVisibility());

      if (!focusableElements?.length) {
        return;
      }

      const focusIsInFlyout = menuFlyout.contains(document.activeElement);

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      // Tabbing backwards...
      if (event.shiftKey) {
        // While focusing on the first item in the flyout, tab back out to the trigger
        if (focusIsInFlyout && firstFocusableElement === document.activeElement) {
          event.preventDefault();
          ownerTrigger.focus();
          // While focusing on the trigger next to the current active trigger,
          // tab back into the end of the flyout
        } else if (flyoutTriggers[ownerTriggerIndex + 1] === document.activeElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
        // Tabbing forwards...
      } else {
        // While focusing on the last item in the flyout, tab out of the flyout to the next trigger
        if (focusIsInFlyout && lastFocusableElement === document.activeElement) {
          event.preventDefault();
          flyoutTriggers[ownerTriggerIndex + 1].focus();
          // While focusing on the trigger, tab into the flyout
        } else if (ownerTrigger === document.activeElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    }
  };

  flyoutTriggers.forEach(trigger => {
    trigger.addEventListener("click", toggleFlyout);
  });

  const closeAll = () => {
    flyoutTriggers.forEach(t => {
      t.ariaExpanded = "false";
    });

    menuSections.forEach(section => {
      section.ariaHidden = "true";
    });

    toggleCollapsible(menuFlyout, { forceState: false });
  };

  document.addEventListener("keydown", event => {
    if (event.key === "Escape") {
      closeAll();
    }
  });

  document.addEventListener("keydown", smartTab);

  document.addEventListener("click", event => {
    const isOutsideFlyout = menuFlyout.contains(event.target as Node) === false;

    const isOutsideTriggers = flyoutTriggers.every(
      trigger => trigger.contains(event.target as Node) === false
    );

    if (isOutsideFlyout && isOutsideTriggers) {
      closeAll();
    }
  });

  menuFlyout.addEventListener("ani:toggle", (e: Event) => {
    const toggleEvent = e as ToggleCollapsibleEvent;

    siteHeader.dispatchEvent(
      new Event(toggleEvent.detail.isOpening ? "flyout:open" : "flyout:close")
    );
  });
}
